import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useEffectOnce } from 'react-use';
import backpack from '~/assets/icons/backpack.svg';
import search from '~/assets/icons/search.svg';
import { PanelControls } from '~/components/ui/PanelControls';
import { SearchInput } from '~/components/ui/SearchInput';
import { ShopListWithScroll } from '~/components/ui/ShopListWithScroll';
import { ShopTuningShopItem } from '~/components/ui/ShopTuningShopItem';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	Currency,
	InputAction,
	InputKey,
	TechTuningItemProperty,
	TechTuningRarity,
	WalletType,
} from '~/shared';
import { useBuy, useTuningDrag } from '../../hooks';
import {
	setActiveItemId,
	setActiveList,
	setActiveListIndex,
	setActiveStage,
	setMaxListIndex,
	setMenuTabsLocked,
	setSearchValue,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	CategoryImages,
	CategoryItemImages,
	CategoryNames,
} from '../CategoryTab';
import { TuningShopRarityGradientColors } from '../ItemColor';
import { getColorOrder } from './ListTab.model';
import {
	StyledControlsContainer,
	StyledEmptyContainer,
	StyledListWrapper,
	StyledSearchContainer,
} from './ListTab.styled';

interface IListTabProps {
	embedded?: boolean;
}

const ListTab = ({ embedded = false }: IListTabProps) => {
	const dispatch = useLayoutDispatch();
	const items = useObservable(rage.techTuning.getItems());
	const shopItems = useObservable(rage.techTuning.getShopItems());
	const ownedItems = useObservable(rage.techTuning.getOwnedItems());
	const installedItemIds = useObservable(rage.techTuning.getInstalledItems());
	const modalState = useObservable(rage.modal.getCurrentModal());
	const activeListIndex = useLayoutSelector(
		(state) => state.tuningShop.activeListIndex,
	);
	const maxListIndex = useLayoutSelector(
		(state) => state.tuningShop.maxListIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.tuningShop.activeCategory,
	);
	const activeItemId = useLayoutSelector(
		(state) => state.tuningShop.activeItemId,
	);
	const tabIndex = useLayoutSelector((state) => state.tuningShop.tabIndex);
	const searchValue = useLayoutSelector(
		(state) => state.tuningShop.searchValue,
	);
	const searchRef = useRef<HTMLInputElement>(null);
	const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const previousActiveItemId = useRef<string>('');
	const rafRef = useRef<number | null>(null);
	const scrollContainerRef = useRef<HTMLElement | null>(null);
	const targetScrollTopRef = useRef<number | null>(null);
	const lastTimeRef = useRef<number | null>(null);
	const { handleBuy } = useBuy();

	const { dragging, handleDragStart, handleDragEnd } = useTuningDrag();

	const installedItemsByCategory = useMemo(() => {
		const map = new Map<string, (typeof items)[0]>();

		for (const installedId of installedItemIds) {
			const item = items.find((i) => i.id === installedId);
			if (item) {
				map.set(item.category, item);
			}
		}

		return map;
	}, [installedItemIds, items]);

	const calculatePropertyDiff = useCallback(
		(item: (typeof items)[0]) => {
			if (!item.properties) {
				return undefined;
			}

			const installedItem = installedItemsByCategory.get(item.category);

			if (!installedItem || !installedItem.properties) {
				return undefined;
			}

			if (installedItem.id === item.id) {
				const zeros: Record<string, number | null> = {};
				const properties = Object.keys(item.properties) as Array<
					keyof typeof item.properties
				>;
				for (const prop of properties) {
					if (item.properties[prop] !== null) {
						zeros[prop] = 0;
					} else {
						zeros[prop] = null;
					}
				}
				return zeros as Record<TechTuningItemProperty, number | null>;
			}

			const diff: Record<string, number | null> = {};

			const properties = Object.keys(item.properties) as Array<
				keyof typeof item.properties
			>;
			for (const prop of properties) {
				const newValue = item.properties[prop];
				const oldValue = installedItem.properties[prop];

				if (newValue !== null && oldValue !== null) {
					diff[prop] = newValue - oldValue;
				} else if (newValue !== null && oldValue === null) {
					diff[prop] = newValue;
				} else {
					diff[prop] = null;
				}
			}

			return diff as Record<TechTuningItemProperty, number | null>;
		},
		[installedItemsByCategory],
	);

	const filteredItems = useMemo(() => {
		const trimmedSearch = searchValue.trim();
		const hasSearchValue = trimmedSearch.length > 0;

		const matchedCategories = hasSearchValue
			? (Object.entries(CategoryNames)
					.filter(([, title]) =>
						title.toLowerCase().includes(trimmedSearch.toLowerCase()),
					)
					.map(([key]) => key as keyof typeof CategoryNames) as Array<
					keyof typeof CategoryNames
			  >)
			: [];

		if (tabIndex === 0) {
			if (hasSearchValue) {
				if (matchedCategories.length > 0) {
					return shopItems.filter((item) =>
						matchedCategories.includes(
							item.category as keyof typeof CategoryNames,
						),
					);
				}

				return shopItems.filter((item) => {
					const matchesName = item.name
						.toLowerCase()
						.includes(trimmedSearch.toLowerCase());
					if (activeCategory === null) return matchesName;
					return matchesName && item.category === activeCategory;
				});
			}

			if (activeCategory === null) {
				return shopItems;
			}

			return shopItems.filter((item) => item.category === activeCategory);
		}

		const ownedItemsInfo = ownedItems.map((item) =>
			items.find((i) => i.id === item.id),
		);

		return ownedItemsInfo
			.filter((item) => {
				if (!item) return false;

				if (hasSearchValue && matchedCategories.length > 0) {
					return matchedCategories.includes(
						item.category as keyof typeof CategoryNames,
					);
				}

				const matchesName = hasSearchValue
					? item.name.toLowerCase().includes(trimmedSearch.toLowerCase())
					: true;
				if (activeCategory === null) return matchesName;
				return matchesName && item.category === activeCategory;
			})
			.sort(
				(a, b) =>
					getColorOrder(a?.rarity || TechTuningRarity.COMMON) -
					getColorOrder(b?.rarity || TechTuningRarity.COMMON),
			);
	}, [searchValue, activeCategory, items, shopItems, ownedItems, tabIndex]);

	const ensureScrollAnimationRunning = useCallback(() => {
		if (rafRef.current !== null) return;

		const tick = (time: number) => {
			const container = scrollContainerRef.current;
			const target = targetScrollTopRef.current;
			if (!container || target === null) {
				rAFStop();
				return;
			}

			const now = time;
			const last = lastTimeRef.current ?? now;
			const dt = Math.max(0, Math.min(1 / 60, (now - last) / 1000));
			lastTimeRef.current = now;

			const current = container.scrollTop;
			const diff = target - current;
			const absDiff = Math.abs(diff);

			const baseSmoothing = 18;
			const maxSmoothing = 42;
			const smoothing =
				baseSmoothing +
				Math.min(1, absDiff / 600) * (maxSmoothing - baseSmoothing);
			const factor = 1 - Math.exp(-smoothing * dt);
			const next = current + diff * factor;
			container.scrollTop = next;

			if (Math.abs(target - next) < 0.2) {
				container.scrollTop = target;
				rAFStop();
				return;
			}

			runNext();
		};

		const runNext = () => {
			rafRef.current = requestAnimationFrame(tick);
		};

		const rAFStop = () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastTimeRef.current = null;
		};

		runNext();
	}, []);

	const scrollToCenter = useCallback(
		(itemId: string) => {
			const itemDiv = document.getElementById(`techTuning-${itemId}`);
			if (!itemDiv) return;

			const container = itemDiv.closest(
				'[data-scroll-container]',
			) as HTMLElement | null;
			if (!container) {
				itemDiv.scrollIntoView({
					block: 'center',
					inline: 'nearest',
					behavior: 'auto',
				});
				previousActiveItemId.current = itemId;
				return;
			}
			scrollContainerRef.current = container;

			const maxScrollTop = container.scrollHeight - container.clientHeight;
			let finalScrollTop: number;

			const isCyclicTransition =
				previousActiveItemId.current &&
				previousActiveItemId.current !== itemId &&
				filteredItems.length > 0;

			if (isCyclicTransition) {
				const currentIndex = filteredItems.findIndex((it) => it?.id === itemId);
				const prevIndex = filteredItems.findIndex(
					(it) => it?.id === previousActiveItemId.current,
				);
				if (prevIndex === filteredItems.length - 1 && currentIndex === 0) {
					finalScrollTop = 0;
				} else if (
					prevIndex === 0 &&
					currentIndex === filteredItems.length - 1
				) {
					finalScrollTop = maxScrollTop;
				} else {
					const c = container.getBoundingClientRect();
					const r = itemDiv.getBoundingClientRect();
					const target =
						container.scrollTop +
						(r.top - c.top) -
						(c.height / 2 - r.height / 2);
					finalScrollTop = Math.max(0, Math.min(target, maxScrollTop));
				}
			} else {
				const c = container.getBoundingClientRect();
				const r = itemDiv.getBoundingClientRect();
				if (c.height <= 0 || r.height <= 0) return;
				const target =
					container.scrollTop + (r.top - c.top) - (c.height / 2 - r.height / 2);
				finalScrollTop = Math.max(0, Math.min(target, maxScrollTop));
			}

			targetScrollTopRef.current = finalScrollTop;
			previousActiveItemId.current = itemId;
			ensureScrollAnimationRunning();
		},
		[filteredItems, ensureScrollAnimationRunning],
	);

	const handleSelectItem = useCallback((index: number | null) => {
		if (index === null) {
			return;
		}

		dispatch(setActiveListIndex(index));
	}, []);

	const handleBack = useCallback(() => {
		dispatch(setActiveStage(StageTypes.Category));
	}, []);

	const handleSearch = useCallback((value: string) => {
		dispatch(setSearchValue(value));
	}, []);

	const handleInstallItem = useCallback(
		(item: (typeof items)[0] | undefined | null) => {
			if (!item) return;
			if (installedItemsByCategory.has(item.category)) return;

			rage.techTuning
				.installItem(item.id)
				.catch((err) => rage.notifications.showException(err));
		},
		[installedItemsByCategory],
	);

	const selectedItem = useMemo(() => {
		if (activeListIndex === null) return null;
		return filteredItems[activeListIndex] || null;
	}, [activeListIndex, filteredItems]);

	const canInstallSelected = useMemo(() => {
		if (tabIndex !== 1) return false;
		if (!selectedItem) return false;
		return !installedItemsByCategory.has(selectedItem.category);
	}, [tabIndex, selectedItem, installedItemsByCategory]);

	const controls = useMemo(() => {
		if (tabIndex === 0) {
			return [
				{
					text: 'Управление',
					keys: [InputKey.ArrowUp, InputKey.ArrowDown],
				},
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
			];
		}

		const inventoryControls: Array<{
			text: string;
			keys: string[];
			onClick?: () => void;
		}> = [
			{
				text: 'Управление',
				keys: [InputKey.ArrowUp, InputKey.ArrowDown],
			},
		];

		if (canInstallSelected && selectedItem) {
			inventoryControls.push({
				text: 'Установить',
				keys: [InputKey.Enter],
				onClick: () => handleInstallItem(selectedItem),
			});
		}

		return inventoryControls;
	}, [
		tabIndex,
		handleBuy,
		canInstallSelected,
		selectedItem,
		handleInstallItem,
	]);

	const categoryInfo = useMemo(() => {
		if (embedded && searchValue.trim().length === 0) {
			return {
				icon: backpack,
				title: 'Инвентарь',
			};
		}

		if (searchValue.trim().length > 0 || !activeCategory) {
			return {
				icon: search,
				title: 'Поиск',
			};
		}

		return {
			icon: CategoryImages[activeCategory],
			title: CategoryNames[activeCategory],
		};
	}, [searchValue, activeCategory, embedded]);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState) {
			return;
		}
		if (tabIndex === 0) {
			handleBuy(WalletType.Cash);
			return;
		}
		if (tabIndex === 1 && canInstallSelected && selectedItem) {
			handleInstallItem(selectedItem);
		}
	});

	useEffect(() => {
		if (activeItemId) {
			scrollToCenter(activeItemId);
		}
	}, [activeItemId, scrollToCenter]);

	useEffect(() => {
		if (activeListIndex !== null && filteredItems[activeListIndex]) {
			const item = filteredItems[activeListIndex];
			if (item && item.id) {
				dispatch(setActiveItemId(item.id));
			}
		}
	}, [activeListIndex, filteredItems]);

	useEffect(() => {
		dispatch(setMaxListIndex(filteredItems.length - 1));
		dispatch(setActiveList(filteredItems));
		previousActiveItemId.current = '';
	}, [filteredItems]);

	useEffectOnce(() => {
		if (searchValue.trim().length > 0) {
			setTimeout(() => {
				searchRef.current?.focus();
			}, 50);
		}

		return () => {
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current);
			}
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
			lastTimeRef.current = null;
			dispatch(setActiveItemId(''));
			dispatch(setActiveListIndex(null));
			dispatch(setSearchValue(''));
		};
	});

	return (
		<Fragment>
			<StyledSearchContainer>
				<SearchInput
					placeholder="Введите название для поиска"
					value={searchValue}
					ref={searchRef}
					onChange={(e) => handleSearch(e.target.value)}
					active={!!searchValue.trim()}
					onFocus={() => dispatch(setMenuTabsLocked(true))}
					onBlur={() => dispatch(setMenuTabsLocked(false))}
				/>
			</StyledSearchContainer>
			<ShopListWithScroll
				icon={categoryInfo.icon}
				title={categoryInfo.title}
				style={{ marginTop: 30 }}
				handleBack={handleBack}
				activeIndex={activeListIndex}
				maxIndex={maxListIndex}
				handleCheckInstall={() => false}
				handleSelect={handleSelectItem}
				height={embedded ? 511 : 491}
				hideBack={embedded}
			>
				<StyledListWrapper>
					{filteredItems.length === 0 && (
						<StyledEmptyContainer>
							{tabIndex === 0 ? (
								<Text>Нет результатов поиска</Text>
							) : (
								<Text>
									У вас нет нужных деталей
									<br />
									для данного транспорта
								</Text>
							)}
						</StyledEmptyContainer>
					)}
					{filteredItems.map((item, index) => {
						if (!item) return null;

						const actualPrice = rage.techTuning.getItemBuyPrice(item.id);
						const oldPrice =
							tabIndex === 1 || !item.discount ? undefined : item.price;
						const sellPrice = rage.techTuning.getItemSellPrice(item.id);

						const propertyDiff = calculatePropertyDiff(item);

						const displayProperties =
							propertyDiff || item.properties || undefined;

						const canInstallItem =
							tabIndex === 1 && !installedItemsByCategory.has(item.category);

						return (
							<ShopTuningShopItem
								key={item.id}
								itemId={item.id}
								title={item.name}
								category={CategoryNames[item.category] || ''}
								image={CategoryItemImages[item.category] || ''}
								price={tabIndex === 1 ? sellPrice : actualPrice}
								// currency={item.currency}
								currency={Currency.Ruble}
								isNew={false}
								imageBg={
									TuningShopRarityGradientColors[
										item.rarity || TechTuningRarity.COMMON
									]
								}
								oldPrice={oldPrice}
								discount={item.discount || 0}
								handleClick={() => handleSelectItem(index)}
								onShiftClick={
									canInstallItem ? () => handleInstallItem(item) : undefined
								}
								active={index === activeListIndex}
								onDragStart={(e) => handleDragStart(e, item)}
								onDragEnd={handleDragEnd}
								dragging={dragging}
								isDraggable={tabIndex === 1}
								dragHint={tabIndex === 1}
								owned={tabIndex === 1}
								count={
									tabIndex === 1
										? ownedItems.find((i) => i.id === item.id)?.count || 1
										: undefined
								}
								properties={displayProperties}
							/>
						);
					})}
				</StyledListWrapper>
			</ShopListWithScroll>
			<StyledControlsContainer>
				<PanelControls controls={controls} align="left" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { ListTab };
